/**
 * FeedbackModal — beta tester bug report / feedback form
 */
import { useState, useEffect, useRef } from 'react';
import Icon from './Icon';
import { useToast } from './ToastContext';
import { submitFeedback } from '../../services/feedbackService';
import { reportError } from '../../utils/sentry';

const FeedbackModal = ({ open, onClose }) => {
  const dialogRef = useRef(null);
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const resetForm = () => {
    setTitle('');
    setCategory('bug');
    setDescription('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || submitting) return;

    setSubmitting(true);
    try {
      const { issueNumber } = await submitFeedback({ title, category, description });
      addToast({ message: `Report submitted (#${issueNumber}). Thank you!`, type: 'success' });
      resetForm();
      onClose();
    } catch (err) {
      reportError(err, { context: 'submit_bug_report' });
      addToast({ message: 'Failed to submit report. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <dialog ref={dialogRef} className="confirm-modal feedback-modal" onClose={handleCancel}>
      <div className="confirm-modal__content">
        <h3 className="confirm-modal__title">
          <Icon name="Bug" size={18} /> Report an Issue
        </h3>
        <form onSubmit={handleSubmit} className="feedback-modal__form">
          <div className="form-group">
            <label className="form-label" htmlFor="feedback-category">Category</label>
            <select
              id="feedback-category"
              className="form-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="bug">Bug</option>
              <option value="suggestion">Suggestion</option>
              <option value="question">Question</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="feedback-title">Title</label>
            <input
              id="feedback-title"
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={200}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="feedback-description">Description</label>
            <textarea
              id="feedback-description"
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened? What did you expect?"
              rows={4}
              maxLength={5000}
              required
            />
          </div>
          <div className="confirm-modal__actions">
            <button type="button" className="button button--outline" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={submitting || !title.trim() || !description.trim()}
            >
              {submitting ? 'Submitting...' : <><Icon name="Send" size={14} /> Submit</>}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
};

export default FeedbackModal;
